"use client"

import { useQuery } from "@tanstack/react-query"
import { useAuth } from "@/hooks/use-auth"
import { listExternalSipPhoneNumbersAction } from "@/server-actions/sip-phone-numbers"
import type { ImportSipPhoneNumberInput } from "@/lib/voice/sip-phone-number-validation"
import type { PhoneNumberLoanAccess, PhoneNumberLoanSummary } from "@/lib/voice/phone-number-loans"

export const pbxPhoneNumbersQueryKey = ["pbx-phone-numbers"] as const

export type PbxPhoneNumber = {
  phone_number: string
  phone_number_sid: string
  friendly_name: string
  capabilities: {
    voice: boolean
    sms: boolean
    mms: boolean
    fax: boolean
  }
  mode: "pbx" | null
  call_flow_id: string | null
  is_enabled: boolean
  outbound_calling_enabled?: boolean
  provider?: "twilio" | "sip_trunk"
  elevenlabs_phone_number_id?: string | null
  sip_form_values?: ImportSipPhoneNumberInput
  loan_access?: PhoneNumberLoanAccess
  loan?: PhoneNumberLoanSummary | null
}

export function usePbxPhoneNumbersQuery(enabled = true) {
  const { fetchWithAuth, user } = useAuth()
  const tenantId =
    user?.impersonated_tenant_id ?? user?.tenant_id ?? "unauthenticated"

  return useQuery<PbxPhoneNumber[]>({
    queryKey: [...pbxPhoneNumbersQueryKey, tenantId],
    enabled,
    queryFn: async () => {
      const [twilioPhones, externalSipPhones] = await Promise.all([
        fetchWithAuth("/api/pbx/phones").then(async (phonesRes) => {
          const data = await phonesRes.json()

          if (!phonesRes.ok) {
            if (data.error === "Twilio integration not configured") return []
            throw new Error(data.error || "Failed to load phone numbers")
          }

          return (data.phones || []) as PbxPhoneNumber[]
        }),
        listExternalSipPhoneNumbersAction({}).then((result) => {
          if ("status" in result) throw new Error(result.errorMessage)
          return result.map((phone): PbxPhoneNumber => ({
            phone_number: phone.phoneNumber,
            phone_number_sid: phone.phoneNumberId ?? phone.phoneNumber,
            friendly_name: phone.label ?? phone.phoneNumber,
            capabilities: {
              voice: true,
              sms: false,
              mms: false,
              fax: false,
            },
            mode: phone.callFlowId ? "pbx" : null,
            call_flow_id: phone.callFlowId,
            is_enabled: Boolean(phone.callFlowId),
            outbound_calling_enabled: phone.outboundCallingEnabled,
            provider: "sip_trunk",
            elevenlabs_phone_number_id: phone.phoneNumberId,
            ...(phone.formValues ? { sip_form_values: phone.formValues } : {}),
            loan_access: phone.access,
            loan: phone.loan,
          }))
        }),
      ])

      return [...twilioPhones, ...externalSipPhones]
    },
  })
}
